// filepath: apps/web/src/features/directory/audit-history/AuditHistoryPage.tsx
//
// FR-UI-DIR-1 AC-10 — tenant-wide directory audit feed viewer.
//
// Reuses the pattern from apps/web/src/features/admin/audit/
// AuditViewerPage (FilterBar + Table + Pagination), but bound to the
// directory audit feed.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { useDirectoryAuditFeed } from "../queries";
import { emptyDirectoryAuditFilter } from "../types";

export function AuditHistoryPage(): React.ReactElement {
  const { t } = useTranslation();
  const filter = emptyDirectoryAuditFilter();
  const query = useDirectoryAuditFeed(filter);

  switch (query.state) {
    case "loading":
      return (
        <p className="directory-audit__loading" data-testid="audit-loading">
          {t("directory.audit.state.loading")}
        </p>
      );
    case "permission-denied":
      return (
        <p
          role="alert"
          className="directory-audit__permission-denied"
          data-testid="audit-permission-denied"
        >
          {t("directory.audit.state.permission_denied")}
        </p>
      );
    case "validation-error":
      return (
        <p
          role="alert"
          className="directory-audit__validation-error"
          data-testid="audit-validation-error"
        >
          {query.error?.message ?? t("directory.audit.state.validation_error")}
        </p>
      );
    case "fetch-failure":
      return (
        <p
          role="alert"
          className="directory-audit__fetch-failure"
          data-testid="audit-fetch-failure"
        >
          {t("directory.audit.state.fetch_failure")}
        </p>
      );
    case "rate-limit":
      return (
        <p
          role="alert"
          className="directory-audit__rate-limit"
          data-testid="audit-rate-limit"
        >
          {t("directory.audit.state.rate_limit")}
        </p>
      );
    case "success": {
      const data = query.data;
      if (data === null) {
        return (
          <p
            role="alert"
            className="directory-audit__fetch-failure"
            data-testid="audit-fetch-failure"
          >
            {t("directory.audit.state.fetch_failure")}
          </p>
        );
      }
      const entries = data.items ?? [];
      return (
        <section className="directory-audit" data-testid="audit-page">
          <header className="directory-audit__header">
            <h1>{t("directory.audit.title", "Directory audit")}</h1>
          </header>
          {entries.length === 0 ? (
            <p className="directory-audit__empty" data-testid="audit-empty">
              {t("directory.audit.empty")}
            </p>
          ) : (
            <table className="directory-audit__table">
              <thead>
                <tr>
                  <th scope="col">
                    {t("directory.audit.column.timestamp", "Timestamp")}
                  </th>
                  <th scope="col">
                    {t("directory.audit.column.actor", "Actor")}
                  </th>
                  <th scope="col">
                    {t("directory.audit.column.action", "Action")}
                  </th>
                  <th scope="col">
                    {t("directory.audit.column.resource", "Resource")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr
                    key={e.id}
                    data-testid={`audit-row-${e.id}`}
                    data-audit-id={e.id}
                  >
                    <td>{e.timestamp}</td>
                    <td>{e.actor_id}</td>
                    <td>{e.action}</td>
                    <td>
                      {e.resource_kind}:{e.resource_id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      );
    }
  }
}
